import './App.css';
import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import NavBar from './components/NavBar/NavBar';
import MobileNavBar from './components/NavBar/MobileNavBar';
import ContactBar from './components/ContactBar/ContactBar';
import ContactBarTwo from './components/ContactBar/ContactBarTwo';
import Contact from './pages/Contact/Contact';
import Gallery from './pages/Gallery/Gallery';
import GutterGuards from './pages/GutterGuards/GutterGuards';
import Home from './pages/Home/Home';
import OurReviews from './pages/OurReviews/OurReviews';
import SeamlessGutters from './pages/SeamlessGutters/SeamlessGutters';
import ServiceAreas from './pages/ServiceAreas/ServiceAreas';
import TheVillages from './pages/TheVillages/TheVillages';
import Footer from './components/Footer/Footer';
import PersistentContactButton from './components/PersistentContactButton/PersistentContactButton';
import ContactModal from './components/ContactModal/ContactModal';
import MobileContactBar from './components/ContactBar/MobileContactBar';
import MobileContactMenu from './components/MobileContactMenu/MobileContactMenu'; // Import MobileContactMenu
import NotFoundPage from './pages/NotFoundPage/NotFoundPage';

import LocalLandingPage from './pages/LocalLandingPage/LocalLandingPage';


import Login from './pages/Login/Login';

import PrivateRoute from './utility/PrivateRoute';

import UserProfile from './pages/User/UserProfile';
import AdminDashboard from './pages/Admin/AdminDashboard';
import BackupCodes from './pages/User/BackupCodes';

import heroBG from './assets/images/hero.webp';
import roofMan from './assets/images/gutterClean.webp';

import gallery1 from './assets/images/gallery/gallery1.webp';
import gallery2 from './assets/images/gallery/gallery2.webp';
import gallery3 from './assets/images/gallery/gallery3.webp';
import gallery4 from './assets/images/gallery/gallery4.webp';
import gallery5 from './assets/images/gallery/gallery5.webp';
import gallery6 from './assets/images/gallery/gallery6.webp';
import gallery7 from './assets/images/gallery/gallery7.webp';
import gallery8 from './assets/images/gallery/gallery8.webp';
import Admin from './pages/Admin/Admin';
import AdminMessages from './pages/Admin/AdminMessages';
import AdminUsers from './pages/Admin/AdminUsers';
import AdminGallery from './pages/Admin/AdminGallery';
import AdminReviews from './pages/Admin/AdminReviews';
import AddEditReview from './pages/Admin/AddEditReview';
import ForgotPassword from './pages/Login/ForgotPassword';
import ResetPassword from './pages/Login/ResetPassword';
import User from './pages/User/User';
import UserTwoFactor from './pages/User/UserTwoFactor';
import Signup from './pages/Login/Signup';
import VerifyEmail from './pages/Login/VerifyEmail';
import RegisterInvite from './pages/Login/RegisterInvite';
import StickyContactBar from './components/ContactBar/StickyContactBar';
import AdminServiceAreas from './pages/Admin/AdminServiceAreas';

import { useServiceAreas } from "./context/ServiceAreaContext";
import AdminServiceAreaPageEditor from './pages/Admin/AdminServiceAreaPageEditor';

import ScrollToTop from './utility/ScrollToTop';
import FreeEstimate from './pages/FreeEstimate/FreeEstimate';

function App() {

  const [isModalOpen, setModalOpen] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false); // Add state for MobileContactMenu visibility
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const { serviceAreas, loading } = useServiceAreas();

  useEffect(() => {
    const imagesToPreload = [
      heroBG,
      roofMan,
      gallery1,
      gallery2,
      gallery3,
      gallery4,
      gallery5,
      gallery6,
      gallery7,
      gallery8
    ];
    imagesToPreload.forEach((imageSrc) => {
      const img = new Image();
      img.src = imageSrc;
    });
  }, []);

  // Function to toggle the modal
  const toggleModal = () => {
    setModalOpen(!isModalOpen);
  };

  // Function to toggle the mobile contact menu
  const toggleContactMenu = () => {
    setMenuOpen(!isMenuOpen); // Toggle visibility
  };

  // Use useEffect to listen for window resizing and update the width
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    
    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setModalOpen(false);  // Close the modal on Escape key press
      }
    };
  
    // Attach event listener for keydown
    document.addEventListener('keydown', handleKeyDown);
  
    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);  

  return (
    <div className="App">
      <ScrollToTop />
      <header role="banner">

        {/* Conditionally render the ContactBar only for desktop screens */}
        {windowWidth >= 785 && <StickyContactBar />}
        {windowWidth >= 785 && <ContactBarTwo />}

        {/* Conditionally render the NavBar or MobileNavBar based on screen width */}
        {windowWidth >= 785 ? <NavBar /> : <MobileNavBar />}
      </header>

      {/* Conditionally render MobileContactBar only on mobile and when menu is not open */}
      {windowWidth < 785 && !isMenuOpen && <MobileContactBar toggleContactMenu={toggleContactMenu} />}
      
      {/* Conditionally render MobileContactMenu */}
      <MobileContactMenu isMenuOpen={isMenuOpen} toggleContactMenu={toggleContactMenu} />

      {/* The button only appears when the modal is closed */}
      {!isModalOpen && !isMenuOpen && <PersistentContactButton onClick={toggleModal} isModalOpen={isModalOpen} />}

      {/* The modal only appears when it's open */}
      {isModalOpen && <ContactModal onClose={toggleModal} isModalOpen={isModalOpen} />}


    
      <main id="main-content" className="content-wrapper">
        <Routes>
          {/* PAGES */}
          <Route path="/" element={<Home />} />
          <Route path="/seamless-gutters" element={<SeamlessGutters />} />
          <Route path="/gutter-guards" element={<GutterGuards />} />
          <Route path="/testimonials" element={<OurReviews />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/the-villages" element={<TheVillages />} />
          <Route path="/service-areas" element={<ServiceAreas />} />


          {/* AUTH */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route path="/register" element={<RegisterInvite />} />
          
          <Route path="/verify-email" element={<VerifyEmail />} />

          <Route element={<PrivateRoute />}>
            <Route path="/admin" element={<Admin />}>


                <Route path="dashboard" element={<AdminDashboard />} />

                <Route path="user" element={<User />}>
                  <Route index element={<Navigate to="/admin/user/profile" replace />} />

                  <Route path="profile" element={<UserProfile />} />
                  <Route path="two-factor" element={<UserTwoFactor />} />

                  <Route path="backup-codes" element={<BackupCodes />} />
                  
                </Route>

                <Route path="users" element={<AdminUsers />} />
                <Route path="messages" element={<AdminMessages />} />
                <Route path="gallery" element={<AdminGallery />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="service-areas" element={<AdminServiceAreas />} />
                <Route path="service-areas/:id/page" element={<AdminServiceAreaPageEditor />} />


                <Route path="reviews/add" element={<AddEditReview />} />
                <Route path="reviews/edit/:id" element={<AddEditReview />} />

            </Route>
          </Route>

          {/* Catch-all service area route */}
         

          <Route path="/free-estimate" element={<FreeEstimate />} />



          <Route path="*" element={<LocalLandingPage />} />
        </Routes>
      </main>



      <Footer />
    </div>
  );
}

export default App;
